import { Insurance } from '@centecomm/portal-service';
import { useInsurance } from '@centecomm/portal-service-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InsuranceActions } from '../actions';
import selectors from '../selectors';

type UseOccupantInsuranceStateHook = (occupantId: number | string) => {
    actionableItems: Insurance[];
    activeItems: Insurance[];
    addPendingDocuments: (insuranceId: number | string, documents: File[]) => void;
    declinedItems: Insurance[];
    editCoverageDates: (insuranceId: number, coverageStart: string, coverageEnd: string) => Promise<void>;
    insuranceCount: number;
    insuranceList: Insurance[];
    isLoaded: boolean;
    isPending: boolean;
    loadInsurance: (occupantId: number | string) => Promise<void>;
    notSubmittedItems: Insurance[];
    pendingItems: Insurance[];
    staticItems: Insurance[];
    upcomingItems: Insurance[];
};

const useOccupantInsuranceState: UseOccupantInsuranceStateHook = (occupantId) => {
    const dispatch = useDispatch();

    const { getInsurances, editInsurance } = useInsurance();

    const [pendingOccupants, setPendingOccupants] = useState<Record<number, boolean>>([]);

    const [isPending, toggleIsPending] = useState<boolean>(false);

    useEffect(() => {
        toggleIsPending(Object.hasOwnProperty.call(pendingOccupants, occupantId) && pendingOccupants[occupantId]);
    }, [pendingOccupants]);

    const actionableItems = useSelector(selectors.actionableInsurances(occupantId));
    const activeItems = useSelector(selectors.activeItems(occupantId));
    const declinedItems = useSelector(selectors.declinedInsurances);
    const insuranceCount = useSelector(selectors.insuranceCount(occupantId));
    const insuranceList = useSelector(selectors.insuranceList(occupantId));
    const isLoaded: boolean = useSelector(selectors.isOccupantLoaded(occupantId));
    const notSubmittedItems = useSelector(selectors.notSubmittedInsurances);
    const pendingItems = useSelector(selectors.pendingInsurances);
    const staticItems = useSelector(selectors.staticItems(occupantId));
    const upcomingItems = useSelector(selectors.upcomingItems(occupantId));

    const loadInsurance = async (): Promise<void> => {
        if (!isPending) {
            setPendingOccupants({
                ...pendingOccupants,
                [occupantId]: true,
            });

            const insurances = await getInsurances(+occupantId);

            dispatch({
                type: InsuranceActions.ADD_OCCUPANT_INSURANCES,
                payload: {
                    occupantId,
                    insurances,
                },
            });

            setPendingOccupants({
                ...pendingOccupants,
                [occupantId]: false,
            });
        }
    };

    const editCoverageDates = async (insuranceId: number, coverageStart: string, coverageEnd: string) => {
        if (!isPending) {
            setPendingOccupants({
                ...pendingOccupants,
                [occupantId]: true,
            });

            await editInsurance(+occupantId, +insuranceId, coverageStart, coverageEnd);

            setPendingOccupants({
                ...pendingOccupants,
                [occupantId]: false,
            });

            await loadInsurance();
        }
    };

    const addPendingDocuments = (insuranceId: number | string, documents: File[]) => {
        dispatch({
            type: InsuranceActions.ADD_PENDING_DOCUMENTS,
            payload: {
                documents,
                insuranceId,
                occupantId,
            },
        });
    };

    return {
        actionableItems,
        activeItems,
        addPendingDocuments,
        declinedItems,
        editCoverageDates,
        insuranceCount,
        insuranceList,
        isLoaded,
        isPending,
        loadInsurance,
        notSubmittedItems,
        pendingItems,
        staticItems,
        upcomingItems,
    };
};

export default useOccupantInsuranceState;
